import React from 'react';

import { useTranslation } from '../../../../hooks/useTranslation/useTranslation';
import { sanitizeUrl } from '../../../../services/urlSafety';
import { getLinkTypeIconPath, isExternalLinkType } from '../../utils/linkTypeUtils';
import styles from './PageSideNav.module.css';
import { PageSideNavProps } from './PageSideNav.types';

export const PageSideNav: React.FC<PageSideNavProps> = ({
  items,
  activeId,
  onItemClick,
  downloadOptions = [],
  className = '',
}) => {
  const { t } = useTranslation();

  const hasItems = Boolean(items && items.length > 0);
  const hasDownloads = Boolean(downloadOptions && downloadOptions.length > 0);

  if (!hasItems && !hasDownloads) return null;

  return (
    <nav
      className={`${styles.sideNavCard} ${className}`.trim()}
      aria-label={t.mods.profile.inPageNavTitle}
      data-testid="page-side-nav"
    >
      {hasItems && (
        <>
          <h3 className={styles.title}>
            <svg
              className={styles.titleIcon}
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
            </svg>
            <span>{t.mods.profile.inPageNavTitle}</span>
          </h3>

          <ul className={styles.navList}>
            {items.map((item) => {
              const isActive = item.id === activeId;
              return (
                <li key={item.id} className={styles.navItem}>
                  <button
                    type="button"
                    className={`${styles.navLink} ${isActive ? styles.navLinkActive : ''}`.trim()}
                    onClick={() => onItemClick?.(item.id)}
                    aria-current={isActive ? 'location' : undefined}
                  >
                    {isActive && <span className={styles.activeDot} aria-hidden="true" />}
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {hasDownloads && (
        <div className={styles.quickDownloadsSection} data-testid="quick-downloads-section">
          <h4 className={styles.quickDownloadsHeader}>
            <svg
              className={styles.quickDownloadsHeaderIcon}
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 20h14v-2H5v2z" />
            </svg>
            <span>{t.mods.profile.quickDownloadsTitle}</span>
          </h4>

          <div className={styles.quickDownloadsList}>
            {downloadOptions.map((opt, index) => {
              const isExternal = isExternalLinkType(opt.linkType);
              return (
                <a
                  key={(opt.id || opt.name) + index}
                  href={sanitizeUrl(opt.link)}
                  target={isExternal ? '_blank' : undefined}
                  rel={isExternal ? 'noopener noreferrer' : undefined}
                  className={styles.quickDownloadBtn}
                  aria-label={
                    isExternal
                      ? `${t.mods.profile.downloadNamed({ name: opt.name })} (${t.mods.thirdPartyNotice})`
                      : t.mods.profile.downloadNamed({ name: opt.name })
                  }
                >
                  <svg
                    className={styles.quickDownloadIcon}
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d={getLinkTypeIconPath(opt.linkType)} />
                  </svg>
                  <span className={styles.quickDownloadName}>{opt.name}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};
